/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { EmbeddedAppConfig, isEmbeddedAppMessageFromIframe } from '@/types/embeddedApp';
import { mimicEnv } from '@/utils/environments';

export interface EmbeddedDrawerState {
  open: boolean;
  eventId: string | null;
  eventTs: string | null;
}

const CLOSED_DRAWER: EmbeddedDrawerState = { open: false, eventId: null, eventTs: null };

export interface UseEmbeddedAppResult {
  /** Ref to attach to the iframe element */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Computed iframe src URL */
  iframeSrc: string;
  /** Whether the iframe has sent the READY message */
  isReady: boolean;
  /** Current environment string */
  env: string;
  /** Whether the iframe has requested fullscreen mode */
  fullscreen: boolean;
  /** Current host drawer state, populated when the iframe sends DRAWER_OPEN */
  drawer: EmbeddedDrawerState;
  /** Close the host drawer and notify the iframe via DRAWER_CLOSE */
  closeDrawer: () => void;
}

/**
 * Extracts origin from a URL string.
 */
function getOriginFromUrl(url: string): string {
  try {
    const parsed = new URL(url);

    return parsed.origin;
  } catch {
    return '';
  }
}

/**
 * Generic hook for managing embedded app iframes with route synchronization.
 *
 * Handles:
 * - Computing iframe src URL from config and current URL params
 * - Listening for postMessage events with origin validation
 * - Sending RESTORE_STATE on iframe READY and browser popstate
 * - Updating browser URL via pushState on ROUTE_CHANGE from iframe
 *
 * @param config - The embedded app configuration
 * @param additionalParams - Additional params to include in the iframe URL (e.g., tenant)
 */
export function useEmbeddedApp(
  config: EmbeddedAppConfig,
  additionalParams?: Record<string, string>,
): UseEmbeddedAppResult {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [drawer, setDrawer] = useState<EmbeddedDrawerState>(CLOSED_DRAWER);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const env = mimicEnv() || 'local';

  // Memoize allowedOrigins to keep handlers stable
  const allowedOrigins = useMemo(() => config.getAllowedOrigins(env), [config, env]);

  // Build params from URL search params and additional params
  const getUrlParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    if (additionalParams) {
      Object.assign(params, additionalParams);
    }

    return params;
  }, [searchParams, additionalParams]);

  // Compute iframe src URL
  const iframeSrc = config.getUrl(env, getUrlParams());

  // Derive target origin from iframe src
  const iframeOrigin = useMemo(() => getOriginFromUrl(iframeSrc), [iframeSrc]);

  // Reset isReady, fullscreen, and drawer when iframe src changes (iframe will remount)
  useEffect(() => {
    setIsReady(false);
    setFullscreen(false);
    setDrawer(CLOSED_DRAWER);
  }, [iframeSrc]);

  const closeDrawer = useCallback(() => {
    setDrawer(CLOSED_DRAWER);
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !iframeOrigin) return;
    iframe.contentWindow.postMessage({ type: 'DRAWER_CLOSE' }, iframeOrigin);
  }, [iframeOrigin]);

  // Send RESTORE_STATE message to iframe
  const sendRestoreState = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !iframeOrigin) return;

    const params = getUrlParams();

    iframe.contentWindow.postMessage({ type: 'RESTORE_STATE', params }, iframeOrigin);
  }, [getUrlParams, iframeOrigin]);

  // Send FULLSCREEN_STATE message to iframe (authoritative echo)
  const sendFullscreenState = useCallback(
    (value: boolean) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || !iframeOrigin) return;

      iframe.contentWindow.postMessage({ type: 'FULLSCREEN_STATE', fullscreen: value }, iframeOrigin);
    },
    [iframeOrigin],
  );

  // Handle incoming messages from iframe
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Strict origin validation
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      // Verify message source is our iframe
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      // Validate message structure
      if (!isEmbeddedAppMessageFromIframe(event.data)) {
        return;
      }

      const message = event.data;

      if (message.type === 'READY') {
        setIsReady(true);
        // Send current state to iframe
        sendRestoreState();
      } else if (message.type === 'NAVIGATE') {
        navigate(message.params?.path);
      } else if (message.type === 'ROUTE_CHANGE') {
        // Use functional form to avoid closing over searchParams
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);

            Object.entries(message.params).forEach(([key, value]) => {
              if (value) {
                newParams.set(key, value);
              } else {
                newParams.delete(key);
              }
            });

            return newParams;
          },
          { replace: false },
        );
      } else if (message.type === 'FULLSCREEN_REQUEST') {
        setFullscreen(message.fullscreen);
        sendFullscreenState(message.fullscreen);
      } else if (message.type === 'DRAWER_OPEN') {
        setDrawer({ open: true, eventId: message.eventId, eventTs: message.eventTs });
      } else if (message.type === 'DRAWER_CLOSE') {
        setDrawer(CLOSED_DRAWER);
      }
    }

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [allowedOrigins, sendRestoreState, sendFullscreenState, setSearchParams, navigate]);

  // While fullscreen is active, the host owns window-level Escape handling
  useEffect(() => {
    if (!fullscreen) return undefined;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setFullscreen(false);
        sendFullscreenState(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreen, sendFullscreenState]);

  // Handle browser back/forward navigation
  // Read params directly from window.location.search since React Router
  // hasn't updated searchParams yet when popstate fires
  useEffect(() => {
    function handlePopState() {
      if (!isReady) return;

      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || !iframeOrigin) return;

      const urlSearchParams = new URLSearchParams(window.location.search);
      const params: Record<string, string> = {};

      urlSearchParams.forEach((value, key) => {
        params[key] = value;
      });

      if (additionalParams) {
        Object.assign(params, additionalParams);
      }

      iframe.contentWindow.postMessage({ type: 'RESTORE_STATE', params }, iframeOrigin);
    }

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [isReady, iframeOrigin, additionalParams]);

  return {
    iframeRef,
    iframeSrc,
    isReady,
    env,
    fullscreen,
    drawer,
    closeDrawer,
  };
}
